import { z } from 'zod'
import { DetectedSignatureSchema } from '../schemas'
import { http } from './http'
import { auth } from './auth'
import { parseWithZod } from '../utils/parse-with-zod'

const DetectedSignatureResponseSchema = z.object({
  detected_signature: DetectedSignatureSchema,
})

export const detectedSignatures = {
  /**
   * Rejects a single detected signature. The backend soft-deletes it and cascades: a signature page
   * left with no signatures and no detected signatures is deleted too, so callers must evict the
   * page from the cache as well.
   */
  delete: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.delete(`${regionHost}/api/detected_signatures/${id}`, dealId)
  },

  /** Coordinates are PDF points anchored at the tab's bottom-left, and must be whole numbers. */
  updateCoordinates: async (
    id: number,
    payload: { x_position: number; y_position: number },
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/detected_signatures/${id}/update_coordinates`,
      parseWithZod(
        z.object({ x_position: z.number().int(), y_position: z.number().int() }),
        payload,
      ),
      dealId,
    )
    return parseWithZod(DetectedSignatureResponseSchema, result).detected_signature
  },

  /**
   * Renames a detected signature's party guesses. Both fields are required but either may be blank;
   * the backend rejects the pair being blank, and rejects a name pair already used on the page.
   */
  updateNames: async (
    id: number,
    payload: { individual_name: string; entity_name: string },
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/detected_signatures/${id}/update_names`,
      payload,
      dealId,
    )
    return parseWithZod(DetectedSignatureResponseSchema, result).detected_signature
  },
}
